import { forwardRef } from 'react';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { FAMILY_CARD_FONT_FAMILY } from '../family-card-form/familyCardFont';
import { useFitFontSize, VALUE_INK_SHADOW } from '../shared/formFieldEmphasis';
import { buildVoterMigrationLetter } from './voterMigrationFormEngine';
import {
    BASELINE_RATIO,
    LEADER,
    LINE_BOX,
    NUMBER_X,
    PAGE1_FIELDS,
    PAGE1_TEXT,
    PAGE2_FIELDS,
    PAGE2_TEXT,
    PAGE_HEIGHT,
    PAGE_WIDTH,
    POST_CODE,
    TYPE,
    VALUE_INSET,
    VALUE_LIFT,
    VALUE_TYPE,
} from './voterMigrationMetrics';

export const VOTER_MIGRATION_PAGE_WIDTH = PAGE_WIDTH;
export const VOTER_MIGRATION_PAGE_HEIGHT = PAGE_HEIGHT;

const LEADER_TEXT = LEADER.char.repeat(LEADER.count);

const PAGE_STYLE = {
    position: 'relative',
    width: PAGE_WIDTH,
    height: PAGE_HEIGHT,
    background: '#fff',
    color: '#111',
    boxSizing: 'border-box',
    fontFamily: FAMILY_CARD_FONT_FAMILY,
    fontSize: TYPE.body.size,
    overflow: 'hidden',
};

/** A measured baseline becomes the CSS top of the line box. */
const topFor = (baseline, size) => baseline - size * BASELINE_RATIO;

/**
 * Rows are placed by baseline. A line box 1.5 em tall leaves room for dots and
 * descenders inside the hidden overflow — padding would do the same in the
 * browser, but html2canvas shifts the text by it, so the export drifted down.
 */
const textStyle = (size, weight) => ({
    position: 'absolute',
    fontSize: size,
    fontWeight: weight,
    lineHeight: LINE_BOX,
    whiteSpace: 'nowrap',
    fontFamily: 'inherit',
});

/** Printed text of the form, anchored by baseline and by left / right / centre. */
function Line({
    y,
    x,
    right,
    center,
    size = TYPE.body.size,
    weight = TYPE.body.weight,
    width,
    className,
    children,
}) {
    const style = { ...textStyle(size, weight), top: topFor(y, size) };
    if (center != null) {
        style.left = center;
        style.transform = 'translateX(-50%)';
    } else if (right != null) {
        style.right = PAGE_WIDTH - right;
    } else {
        style.left = x;
    }
    if (width != null) {
        style.width = width;
        style.whiteSpace = 'normal';
        style.lineHeight = 1.85;
    }
    return <span className={className} style={style}>{children}</span>;
}

/** One printed leader: real dots, clipped to the span the form gives it. */
function Leader({ y, from, to, size = TYPE.body.size }) {
    return (
        <span
            aria-hidden
            data-leader
            style={{
                ...textStyle(size, TYPE.body.weight),
                top: topFor(y, size),
                left: from,
                width: Math.max(0, to - from),
                overflow: 'hidden',
                letterSpacing: LEADER.letterSpacing,
            }}
        >
            {LEADER_TEXT}
        </span>
    );
}

/**
 * A typed answer, sitting just above its dotted line and clipped to it. It
 * prints bigger and heavier than the form's own text, and drops back toward the
 * body size only when the blank is too short for the answer.
 */
function Value({ y, from, to, children }) {
    const maxWidth = Math.max(0, to - from - VALUE_INSET - 2);
    const [ref, size] = useFitFontSize(VALUE_TYPE.size, VALUE_TYPE.minSize, [children, maxWidth]);

    if (!children) return null;
    return (
        <span
            ref={ref}
            data-field-value
            data-filled="true"
            className="voter-migration-field-value"
            style={{
                ...textStyle(size, VALUE_TYPE.weight),
                top: topFor(y - VALUE_LIFT, size),
                // A little air after the printed label, like a written form.
                left: from + VALUE_INSET,
                maxWidth,
                textShadow: VALUE_INK_SHADOW,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            }}
        >
            {children}
        </span>
    );
}

/** Label, leader and value for one blank on the form. */
function Field({ field, t, values }) {
    const label = t(`voterMigrationForm.preview.${field.label}`);
    return (
        <>
            {field.num ? <Line y={field.y} x={NUMBER_X}>{`${field.num}।`}</Line> : null}
            {field.anchorRight ? (
                <Line className="voter-migration-field-label" y={field.y} right={field.from - 3}>{label}</Line>
            ) : (
                <Line className="voter-migration-field-label" y={field.y} x={field.x}>{label}</Line>
            )}
            <Leader y={field.y} from={field.from} to={field.to} />
            {field.value ? (
                <Value y={field.y} from={field.from} to={field.to}>{values[field.value]}</Value>
            ) : null}
        </>
    );
}

/**
 * The four printed cells. The digits are drawn the same way as every other line
 * — absolutely placed on a baseline — because html2canvas does not centre flex
 * children, which pushed them to the bottom of the boxes on export.
 */
function PostCodeBoxes({ value }) {
    const digits = String(value || '').padEnd(4, ' ').slice(0, 4).split('');
    const digitBaseline = POST_CODE.boxTop + POST_CODE.boxHeight * 0.60;
    return (
        <>
            {digits.map((digit, index) => (
                <span
                    // eslint-disable-next-line react/no-array-index-key
                    key={`cell-${index}`}
                    aria-hidden
                    style={{
                        position: 'absolute',
                        left: POST_CODE.boxLeft + index * POST_CODE.boxWidth,
                        top: POST_CODE.boxTop,
                        width: POST_CODE.boxWidth,
                        height: POST_CODE.boxHeight,
                        border: '1px solid #111',
                        borderLeftWidth: index === 0 ? 1 : 0,
                        boxSizing: 'border-box',
                    }}
                />
            ))}
            {digits.map((digit, index) => (digit.trim() ? (
                <span
                    // eslint-disable-next-line react/no-array-index-key
                    key={`digit-${index}`}
                    data-field-value
                    data-filled="true"
                    style={{
                        ...textStyle(VALUE_TYPE.size, VALUE_TYPE.weight),
                        textShadow: VALUE_INK_SHADOW,
                        top: topFor(digitBaseline, VALUE_TYPE.size),
                        // Centred by width, not by transform: html2canvas drops
                        // transforms and the digits slid off centre on export.
                        left: POST_CODE.boxLeft + index * POST_CODE.boxWidth,
                        width: POST_CODE.boxWidth,
                        textAlign: 'center',
                    }}
                >
                    {digit.trim()}
                </span>
            ) : null))}
        </>
    );
}

function PageOne({ d, t }) {
    const p = PAGE1_TEXT;
    return (
        <div data-voter-migration-page style={PAGE_STYLE}>
            <Line y={p.title.y} center={p.title.center} size={TYPE.title.size} weight={TYPE.title.weight}>
                {t('voterMigrationForm.preview.formNo')}
            </Line>
            <Line y={p.rule.y} center={p.rule.center} size={TYPE.rule.size} weight={TYPE.rule.weight}>
                {t('voterMigrationForm.preview.rule')}
            </Line>
            <Line y={p.mainTitle.y} center={p.mainTitle.center} size={TYPE.mainTitle.size} weight={TYPE.mainTitle.weight}>
                {t('voterMigrationForm.preview.title')}
            </Line>

            <Line y={p.to.y} x={p.to.x}>{t('voterMigrationForm.preview.to')}</Line>
            <Line y={p.officer.y} x={p.officer.x}>{t('voterMigrationForm.preview.officer')}</Line>

            <Line y={p.nidNote.y} right={p.nidNote.right} size={TYPE.note.size} weight={TYPE.note.weight}>
                {t('voterMigrationForm.preview.nidCopyNote')}
            </Line>

            <Line y={p.currentTitle.y} x={p.currentTitle.numX}>{`${p.currentTitle.num}।`}</Line>
            <Line y={p.currentTitle.y} x={p.currentTitle.x}>{t('voterMigrationForm.preview.currentTitle')}</Line>

            <Line y={p.newTitle.y} x={p.newTitle.numX}>{`${p.newTitle.num}।`}</Line>
            <Line y={p.newTitle.y} x={p.newTitle.x}>{t('voterMigrationForm.preview.newTitle')}</Line>

            {PAGE1_FIELDS.map((field) => (
                <Field key={`${field.y}-${field.from}`} field={field} t={t} values={d} />
            ))}

            <Line y={POST_CODE.y} right={POST_CODE.labelRight}>
                {t('voterMigrationForm.preview.postCodeLabel')}
            </Line>
            <PostCodeBoxes value={d.postCode} />
        </div>
    );
}

function PageTwo({ d, t }) {
    const p = PAGE2_TEXT;
    const docs = t('voterMigrationForm.preview.docs', { returnObjects: true });
    const docList = Array.isArray(docs) ? docs : [];

    return (
        <div data-voter-migration-page style={PAGE_STYLE}>
            <Line y={p.docsHead.y} x={p.docsHead.numX}>{`${p.docsHead.num}।`}</Line>
            <Line y={p.docsHead.y} x={p.docsHead.x}>{t('voterMigrationForm.preview.docsTitle')}</Line>

            {docList.map((item, index) => {
                const spot = p.docs[index];
                if (!spot) return null;
                // The first clause runs onto a second, indented line on the original.
                return (
                    <Line key={item} y={spot.y} x={spot.x} width={spot.wrapX ? 460 : undefined}>
                        {item}
                    </Line>
                );
            })}

            <Leader y={p.applicantSignature.leaderY} from={p.applicantSignature.from} to={p.applicantSignature.to} />
            <Line y={p.applicantSignature.y} center={p.applicantSignature.center}>
                {t('voterMigrationForm.preview.applicantSignature')}
            </Line>

            {PAGE2_FIELDS.map((field) => (
                <Field key={`${field.y}-${field.from}`} field={field} t={t} values={d} />
            ))}

            <Line y={p.officeOnly.y} center={p.officeOnly.center}>
                {t('voterMigrationForm.preview.officeOnly')}
            </Line>

            <Line y={p.decision.line1.y} x={p.decision.line1.x}>
                {t('voterMigrationForm.preview.officeDecisionPrefix')}
            </Line>
            <Leader y={p.decision.line1.y} from={p.decision.line1.from} to={p.decision.line1.to} />
            <Line y={p.decision.line1.y} x={p.decision.line1.tailX}>
                {t('voterMigrationForm.preview.officeDecisionMiddle')}
            </Line>
            <Line y={p.decision.line2.y} x={p.decision.line2.x}>
                {t('voterMigrationForm.preview.officeDecisionTail')}
            </Line>
            <Leader y={p.decision.line2.y} from={p.decision.line2.from} to={p.decision.line2.to} />
            <Line y={p.decision.line2.y} x={p.decision.line2.tailX}>
                {t('voterMigrationForm.preview.officeDecisionSuffix')}
            </Line>

            <Leader y={p.officerSignature.leaderY} from={p.officerSignature.from} to={p.officerSignature.to} />
            <Line y={p.officerSignature.y} center={p.officerSignature.center}>
                {t('voterMigrationForm.preview.officerSignature')}
            </Line>
            <Leader y={p.officerDate.leaderY} from={p.officerDate.from} to={p.officerDate.to} />

            <Leader y={p.separator.y} from={p.separator.from} to={p.separator.to} />
            <Line y={p.receiptTitle.y} center={p.receiptTitle.center} weight={700}>
                {t('voterMigrationForm.preview.receiptTitle')}
            </Line>

            <Line y={p.receiptName.y} x={p.receiptName.x}>
                {t('voterMigrationForm.preview.receiptNamePrefix')}
            </Line>
            <Leader y={p.receiptName.y} from={p.receiptName.from} to={p.receiptName.to} />
            <Value y={p.receiptName.y} from={p.receiptName.from} to={p.receiptName.to}>
                {d.applicantName}
            </Value>
            <Line y={p.receiptName.y} x={p.receiptName.tailX}>
                {t('voterMigrationForm.preview.receiptNameSuffix')}
            </Line>

            <Line y={p.receiptFormNo.y} x={p.receiptFormNo.x}>
                {t('voterMigrationForm.preview.receiptFormNoLabel')}
            </Line>
            <Leader y={p.receiptFormNo.y} from={p.receiptFormNo.from} to={p.receiptFormNo.to} />

            <Leader y={p.receiverSignature.leaderY} from={p.receiverSignature.from} to={p.receiverSignature.to} />
            <Line y={p.receiverSignature.y} center={p.receiverSignature.center}>
                {t('voterMigrationForm.preview.receiverSignature')}
            </Line>
        </div>
    );
}

function VoterMigrationFormPreview({ form }, ref) {
    const { t } = useTranslation();
    const d = buildVoterMigrationLetter(form);

    return (
        <div ref={ref} data-voter-migration-document style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PageOne d={d} t={t} />
            <PageTwo d={d} t={t} />
        </div>
    );
}

export default forwardRef(VoterMigrationFormPreview);
